// Centralised axios configuration. [IT-771]
//
// Extracted from main.js so the request timeout and the response error
// handling can be unit-tested without mounting the whole app (main.js mounts
// on import, so it was never importable from a spec). Behaviour of the success
// and HTTP-status error paths is unchanged from the inline interceptors.

// IT-771: axios has no default timeout. A request whose response never
// completes back to the browser — a dropped or interrupted connection, routine
// on the offshore links the Tiderise tagging team works over — therefore never
// settles, so submitGroup's `finally { isSubmitting = false }` never runs and
// the button spins forever with no error and no recovery but a reload, even
// though the server already committed the save. A finite timeout turns that
// into a normal rejection the existing per-view catch blocks already surface
// as "try again". 60s is well above our slowest real request (get_groups p99
// well under 1s) so it only ever fires on a genuinely dead connection.
export const REQUEST_TIMEOUT_MS = 60000

// DRF field errors are {field: ["message"]}, but not every error has that
// shape — a plain {"detail": "..."} used to render as its first character, and
// a list of objects as "[object Object]". Coerce whatever comes back into
// something a human can read.
export function readable(value) {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const first = value[0]
    return typeof first === 'string' ? first : JSON.stringify(first)
  }
  return JSON.stringify(value)
}

export function handleResponseSuccess(res, commit) {
  if (res.status === 201) {
    commit('setMessage', {text: 'Creation Successful', type: 'success', alert: true})
  }
  if (res.config.method === 'patch') {
    commit('setMessage', {text: 'Update Successful', type: 'success', alert: true})
  }
  if (res.config.method === 'delete') {
    commit('setMessage', {text: 'Successfully Deleted', type: 'error', alert: true})
  }
  return res
}

export function handleResponseError(err, commit) {
  // IT-771: a timeout or a dropped connection rejects with NO `err.response`.
  // The old handler read `err.response.status` unconditionally and threw a
  // TypeError on exactly these errors, so a network failure surfaced as an
  // opaque crash instead of a readable message. Handle it first, and rethrow
  // the ORIGINAL error so the per-view catch blocks (e.g. submitGroup's) still
  // fire and clear their busy flags.
  if (!err.response) {
    const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')
    commit('setMessage', {
      text: isTimeout
        ? 'The server took too long to respond — your last action may not have saved. Please check your connection and try again.'
        : 'Could not reach the server — your last action may not have saved. Please check your connection and try again.',
      type: 'error',
      alert: true,
    })
    throw err
  }

  if (err.response.status === 400) {
    const data = err.response.data
    if (typeof data === 'string') {
      commit('setMessage', {text: data, type: 'error', alert: true})
    } else if (data && data.detail) {
      // Prefer the human-facing detail over dumping every diagnostic key.
      commit('setMessage', {text: readable(data.detail), type: 'error', alert: true})
    } else {
      Object.keys(data || {}).forEach(key => {
        commit('setMessage', {text: readable(data[key]), type: 'error', alert: true})
      })
    }
    throw err
  }

  if (err.response.status === 404 && err.response.data === 'Login Incorrect') {
    commit('setMessage', {text: err.response.data, type: 'error', alert: true})
    throw err
  }

  throw err
}

export function configureAxios(axios, store) {
  const commit = (mutation, payload) => store.commit(mutation, payload)

  // IT-594: each tenant scopes its CSRF cookie to its own name (see backend
  // settings/base.py). VUE_APP_CSRF_COOKIE_NAME is set per-tenant at Docker
  // build time (see Dockerfile + scripts/new-platform.sh). Defaults to
  // 'csrftoken' for local dev, where backend runs settings.development.
  axios.defaults.xsrfCookieName = process.env.VUE_APP_CSRF_COOKIE_NAME || 'csrftoken'
  axios.defaults.xsrfHeaderName = 'X-CSRFToken'
  axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL
  axios.defaults.headers.post['Content-Type'] = 'application/json'
  axios.defaults.timeout = REQUEST_TIMEOUT_MS

  axios.interceptors.request.use(
    (config) => {
      const token = store.state.auth.token
      if (token) {
        config.headers['Authorization'] = `Token ${token}`
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  axios.interceptors.response.use(
    (res) => handleResponseSuccess(res, commit),
    (err) => handleResponseError(err, commit),
  )
}
